import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { HTTPError, post } from "common/helpers/HTTP";
import { parseLeadsSearchParams } from "common/helpers/utils";
import type {
  CrmExportType,
  LeadsSearchParams,
  OutreachSourceType,
  SelectedFacetParam,
} from "common/types";

import type { OUTREACH_CRM_EXPORT_OPTIONS } from "../constants";
import type { CrmExportSummary, CrmIntegrationExportResult } from "../types";

async function exportLeadsRequest(
  workspaceId: string,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
): Promise<string> {
  const { url } = await post(
    `workspaces/${workspaceId}/leads-export`,
    parseLeadsSearchParams(searchParams),
  );
  return url;
}

async function exportLeadsToCrmIntegrationRequest(
  workspaceId: string,
  crmExportType: CrmExportType,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
): Promise<CrmIntegrationExportResult> {
  const { job_id, export: exportSummary } = await post(
    `workspaces/${workspaceId}/integrations/${crmExportType}/leads-export`,
    parseLeadsSearchParams(searchParams),
  );

  if (exportSummary) {
    return { kind: "sync", summary: exportSummary };
  }

  if (job_id) {
    return { kind: "async", jobId: job_id };
  }

  return { kind: "immediate" };
}

async function exportLeadsToOutreachCrmRequest(
  workspaceId: string,
  outreachSourceType: OutreachSourceType,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
  type: keyof typeof OUTREACH_CRM_EXPORT_OPTIONS,
  id: string,
): Promise<CrmExportSummary> {
  return (
    await post(
      `workspaces/${workspaceId}/integrations/${outreachSourceType}/leads-export`,
      { ...parseLeadsSearchParams(searchParams), [`${type}_id`]: id },
    )
  ).export;
}

export default function useLeadExports() {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { mutateAsync: exportLeads, isPending: isExportingLeads } = useMutation(
    {
      mutationFn: ({
        searchParams,
      }: {
        searchParams: LeadsSearchParams<SelectedFacetParam>;
      }) => exportLeadsRequest(workspaceId, searchParams),
    },
  );

  const {
    mutateAsync: exportLeadsToCrmIntegration,
    isPending: isExportingLeadsToCrmIntegration,
  } = useMutation({
    mutationFn: ({
      searchParams,
      crmExportType,
    }: {
      searchParams: LeadsSearchParams<SelectedFacetParam>;
      crmExportType: CrmExportType;
    }) =>
      exportLeadsToCrmIntegrationRequest(
        workspaceId,
        crmExportType,
        searchParams,
      ),
  });

  const {
    mutateAsync: exportLeadsToOutreachCrm,
    isPending: isExportingLeadsToOutreachCrm,
  } = useMutation({
    mutationFn: ({
      type,
      searchParams,
      sourceType,
      id,
    }: {
      type: OutreachSourceType;
      searchParams: LeadsSearchParams<SelectedFacetParam>;
      sourceType: keyof typeof OUTREACH_CRM_EXPORT_OPTIONS;
      id: string;
    }) =>
      exportLeadsToOutreachCrmRequest(
        workspaceId,
        type,
        searchParams,
        sourceType,
        id,
      ),
    onSuccess: (_, { type }) => {
      const label = `${type.charAt(0).toUpperCase()}${type.slice(1)}`;

      toast.success(`Successfully exported leads to ${label}`);
    },
    onError: (error, { type }) => {
      const label = `${type.charAt(0).toUpperCase()}${type.slice(1)}`;
      let errorMessage = `Failed to export leads to ${label}`;

      if (error instanceof HTTPError) {
        if (error.type === "Unauthorized") {
          errorMessage = `${label} API token expired. Update your API key to resolve this`;
        } else if (error.type === "Too Many Requests") {
          errorMessage = `${label} rate limit hit. Please try again later`;
        } else if (error.type === "Bad Request") {
          errorMessage =
            "Export failed: No valid leads found. Make sure uploaded leads have an email address";
        }
      }

      toast.error(errorMessage);
    },
  });

  return {
    exportLeads,
    isExportingLeads,
    exportLeadsToCrmIntegration,
    isExportingLeadsToCrmIntegration,
    exportLeadsToOutreachCrm,
    isExportingLeadsToOutreachCrm,
  };
}
